import React, { useState, useEffect, useMemo } from 'react';
import { ORDER_NOTES } from '@/components/OrderDetailsPage/queries';

import { Grid, Typography, Button } from '@material-ui/core';
import { css } from '@emotion/react';

import { OnwardCard } from '@/components/OrderDetailsPage/Card';
import { useClientUser } from '@/hooks';
import { greys } from '@/styles/colors';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/react';
import { colors } from '@/styles';
import { startCase } from 'lodash';
import { useQuery } from '@apollo/client';

const dateShort = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});

const timestamp = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
});

export const HR = styled.div`
    width: 100%;
    margin-top: 22px;
    margin-bottom: 22px;
    border: 1px solid #e2e2e2;
`;

export const Note = (note, isLast, accountTypeCapital, compact = false) => {
    const source_user_type = note?.source_user_type;

    return (
        <>
            <Grid container direction="row">
                <Grid container item direction="column" xs={compact ? 6 : 4}>
                    {note?.private_to === accountTypeCapital && (
                        <Grid item>
                            <Typography
                                css={css`
                                    color: orange;
                                    font-weight: 500;
                                `}
                            >
                                Private
                            </Typography>
                        </Grid>
                    )}

                    {note?.requires_acknowledgement && (
                        <Grid item>
                            <Typography
                                css={css`
                                    color: ${colors.greens.primary};
                                    font-weight: 500;
                                `}
                            >
                                Onward/Shipper Notified
                            </Typography>
                        </Grid>
                    )}

                    <Grid item>
                        <Typography
                            css={css`
                                font-weight: 500;
                                line-break: anywhere;
                            `}
                        >
                            {source_user_type
                                ? source_user_type === 'Admin'
                                    ? 'Onward'
                                    : `(${source_user_type}) ${note?.user?.username}`
                                : 'System'}
                        </Typography>
                    </Grid>

                    {note.source_user_type !== 'Admin' && (
                        <Grid item>
                            <Typography
                                css={css`
                                    color: ${greys.primary};
                                    font-weight: 500;
                                    line-break: anywhere;
                                `}
                            >
                                {note?.user?.email}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item>
                        <Typography
                            css={css`
                                color: ${greys.primary};
                                font-weight: 500;
                            `}
                        >
                            {dateShort.format(new Date(note.created_at))}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography
                            css={css`
                                color: ${greys.primary};
                                font-weight: 500;
                            `}
                        >
                            {timestamp.format(new Date(note.created_at))}
                        </Typography>
                    </Grid>
                </Grid>

                <Grid container direction="column" item xs={compact ? 6 : 8}>
                    {note?.type && (
                        <Grid item>
                            <Typography
                                css={css`
                                    font-weight: 500;
                                `}
                            >
                                {note?.type}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item>
                        <Typography
                            css={css`
                                font-weight: 500;
                                ${note?.is_acknowledgement &&
                                `
                                    font-style: italic;    
                                `}
                            `}
                        >
                            {note?.is_acknowledgement ? `Acknowledged by ${note?.source_user_type}` : note.note}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>

            {!isLast && <HR />}
        </>
    );
};

function OrderNotes({ order }) {
    const { accountType, isOnwardAdmin } = useClientUser();

    const privacyExp = useMemo(() => {
        if (isOnwardAdmin) return {};
        return { _or: [{ private_to: { _is_null: true } }, { private_to: { _eq: startCase(accountType) } }] };
    }, [isOnwardAdmin, accountType]);

    const { loading: notesLoading, data: notesData } = useQuery(ORDER_NOTES, {
        variables: {
            order_id: order.order_id,
            notes_privacy: privacyExp,
        },
    });

    const notes = useMemo(() => {
        return notesData?.notes || [];
    }, [notesData]);

    const accountTypeCapital = startCase(accountType);

    return (
        <>
            <Grid item sm={1}></Grid>
            <Grid
                container
                sm={10}
                css={css`
                    height: 100%;
                    align-content: flex-start;
                `}
            >
                {!notes?.length ? (
                    <OnwardCard
                        css={css`
                            margin-bottom: 30px;
                        `}
                    >
                        <span
                            css={css`
                                font-style: italic;
                            `}
                        >
                            No notes
                        </span>
                    </OnwardCard>
                ) : (
                    <>
                        <OnwardCard>
                            {(notes || [])?.map((note, idx, arr) =>
                                Note(note, arr.length - 1 === idx, accountTypeCapital)
                            )}
                        </OnwardCard>
                    </>
                )}
            </Grid>
            <Grid item sm={1}></Grid>
        </>
    );
}

export default OrderNotes;
